// src/features/insights/api.ts
//
// Data fetcher for the Momentum Insights dashboard. Talks to the FastAPI
// breakout-alerts router via the shared `apiClient` axios instance, which:
//   - attaches the WordPress-issued JWT (Authorization: Bearer <token>) so
//     the auth-gated /api/v1/breakout-alerts router is reached as an
//     authorized user (the whole router sits behind a router-level
//     Depends(get_current_user) in main.py), and
//   - already bakes the `/api/v1` version prefix into its baseURL, plus the
//     silent-refresh / 401-bounce contract.
// We therefore request the bare `/breakout-alerts/...` path here — adding
// `/api/v1` would double the prefix. See api/client.ts (REQUIRED_API_PREFIX).
//
// The backend serves this entirely off the `ix_breakout_alerts_signal_vol`
// composite index (no live FMP fetch, no Numba math on the request path), so
// the round-trip is dominated by network latency, not compute.

import { apiClient } from '../../api/client';

// Matches the exact JSON row shape emitted by BreakoutAlertDTO. The `signal`
// is one of the AlertSignal enum values (STRONG_BUY / BUY_WATCH /
// HOLD_CONSOLIDATING / SELL_WATCH / STRONG_SELL / INSUFFICIENT_DATA) and
// `asset_class` is 'equity' or 'etf'. Optional numeric fields are dropped by
// the serializer when null, so they are modelled as nullable here.
export interface BreakoutAlertDTO {
    symbol: string;
    asset_class: string;
    signal: string;
    close_price: number;
    prev_20d_high?: number | null;
    prev_20d_low?: number | null;
    sma_50?: number | null;
    sma_200?: number | null;
    volume_surge_multiplier?: number | null;
    updated_at: string;
    name?: string | null;
    exchange?: string | null;
    currency?: string | null;
    region?: 'US' | 'EU' | string | null;
    market_cap_usd?: number | null;
}

export interface BreakoutInsightsResponse {
    top_buys: BreakoutAlertDTO[];
    top_sells: BreakoutAlertDTO[];
    execution_time_ms: number;
}

// Fetch the two pre-ranked leaderboards (top buys + top sells). Resolves to
// GET /api/v1/breakout-alerts/insights/top?limit=<limit>. Returns null on any
// failure so the page can render a graceful error state rather than throwing
// (the axios interceptor already owns the 401 -> silent-refresh / WP-bounce
// path centrally).
export const fetchBreakoutInsights = async (
    limit = 10,
): Promise<BreakoutInsightsResponse | null> => {
    try {
        const { data } = await apiClient.get<BreakoutInsightsResponse>(
            '/breakout-alerts/insights/top',
            { params: { limit } },
        );
        return data;
    } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch breakout insights:', error);
        return null;
    }
};

// --- CRYPTO INSIGHTS ---------------------------------------------------------
// A flattened swing-trading breakout row emitted by CryptoInsightItem. `action`
// is one of BUY / ACCUMULATE / SELL / AVOID / TAKE PROFIT; nullable numerics are
// dropped by the serializer when absent, so they are modelled as nullable here.
export interface CryptoInsightItem {
    symbol: string;
    action: string;
    signal: string;
    close_price: number | null;
    expected_return_pct: number | null;
    rsi_14: number | null;
    reasoning: string;
}

export interface CryptoInsightsResponse {
    top_buys: CryptoInsightItem[];
    top_sells: CryptoInsightItem[];
    updated_at: string;
}

// Fetch the pre-computed Top-5 crypto Buys/Sells. Resolves to
// GET /api/v1/crypto-info/insights/top, served instantaneously off Redis
// (populated by the nightly sync_crypto_insights ARQ worker). Uses the shared
// `apiClient` (NOT raw fetch) so the WordPress JWT + silent-refresh contract is
// honoured — the /crypto-info router is auth-gated. Returns null on any failure
// so the page can degrade gracefully.
export const fetchCryptoInsights =
    async (): Promise<CryptoInsightsResponse | null> => {
        try {
            const { data } = await apiClient.get<CryptoInsightsResponse>(
                '/crypto-info/insights/top',
            );
            return data;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch crypto insights:', error);
            return null;
        }
    };
